import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  AffiliatesListQuery,
  AffiliatesListResponseData,
  ApiSuccessEnvelope,
} from "../../types/affiliates";
import { getPrivateInstance } from "../_privateInstance";

// API caps `limit` between 1 and 100; 20 reads comfortably in the list UI.
const DEFAULT_LIMIT = 20;

const fetchAffiliatesList = async (
  params: AffiliatesListQuery,
): Promise<AffiliatesListResponseData> => {
  const response = await getPrivateInstance().get<
    ApiSuccessEnvelope<AffiliatesListResponseData>
  >(API_ROUTES.LIST_AFFILIATES, {
    params: {
      limit: params.limit ?? DEFAULT_LIMIT,
      // First page is signalled by an absent cursor — empty strings get
      // rejected at the schema layer, so guard against `""` too.
      ...(params.last_evaluated_key
        ? { last_evaluated_key: params.last_evaluated_key }
        : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.plan_type ? { plan_type: params.plan_type } : {}),
      // `q` does a substring match across company / contact name, email,
      // and affiliate id.
      ...(params.search?.trim() ? { q: params.search.trim() } : {}),
      // Date range scopes the per-row `metrics` + `portfolio_kpis` aggregate.
      // Enforces both-or-neither, so only send the pair when complete.
      ...(params.start_date && params.end_date
        ? { start_date: params.start_date, end_date: params.end_date }
        : {}),
    },
  });

  return response.data.data;
};

export const useAffiliatesList = (
  params: AffiliatesListQuery = {},
): UseQueryResult<AffiliatesListResponseData, Error> =>
  useQuery<AffiliatesListResponseData, Error>({
    queryKey: [
      "affiliates",
      "list",
      params.limit ?? DEFAULT_LIMIT,
      params.last_evaluated_key ?? "",
      params.status ?? "",
      params.plan_type ?? "",
      params.search?.trim() ?? "",
      params.start_date ?? "",
      params.end_date ?? "",
    ],
    queryFn: () => fetchAffiliatesList(params),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });

export { fetchAffiliatesList };
